import { useConfig } from '@components/config-provider/src';
import { formatDate, toDate } from '@components/date-picker/src/DatePickerUtils';
import { useRenderLabel } from '@components/date-time-picker/src/UseRenderLabel';
import { usePropsValue } from '@hooks/UsePropsValue';
import { DatePicker, Input } from '@tinper/m';
import Calendar from '@tinper/m-icons/lib/cjs/Calendar';
import { withNativeProps } from '@utils/NativeProps';
import { mergeProps } from '@utils/WithDefaultProps';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { useState } from 'react';
import type { TimePickerProps } from './iTimePicker';

const defaultProps = {
  browser: false,
  disabled: false,
  readOnly: false,
  use12Hours: false,
  subuitype: 'HH:mm:ss',
  hourStep: 1,
  minuteStep: 1,
  secondStep: 1,
  showClear: true
}

function TimePicker(props: TimePickerProps) {
  const { locale } = useConfig();
  const p = mergeProps(defaultProps, props) ;
  const {
    browser,
    readOnly,
    disabled,
    clsPrefix,
    placeholder = locale.TimePicker.placeholder,
    popTitle = locale.TimePicker.popTitle,
    textAlign,
    suffix,
  } = p;
  const classPrefix = `${clsPrefix}-time-picker`;
  const [visible, setVisible] = useState(false);

  const [value, setValue] = usePropsValue({
    value: isNil(p.value) ? p.value : toDate(p.value),
    defaultValue: p.defaultValue === undefined ? null : toDate(p.defaultValue),
  });

  const renderLabel = useRenderLabel()

  const handleConfirm = (date: Date  | null) => {
    setVisible(false);
    setValue(date);
    p.onOk?.(date);
  };

  const handleCancel = () => {
    setVisible(false);
    p.onDismiss?.();
  };

  const handleDismiss = () => {
    setVisible(false);
    setValue(null);
    p.onClearReturn?.();
  };

  const filterValue = {
    hour: (h: number) => h % p.hourStep === 0,
    minute: (m: number) => m % p.minuteStep === 0,
    second: (s: number) => s % p.secondStep === 0,
  };

  const precision = p.subuitype === 'HH:mm' ? 'minute' : 'second';

  // 默认后缀图标
  const defaultSuffix = (
    <Calendar
      style={{
        width: '0.44rem',
        height: '0.44rem',
      }}
    />
  );

  const renderInput = (date: Date | string | null) => (
    <div
      className={classPrefix}
      onClick={() => setVisible(disabled ? false : !visible)}
    >
      <Input
        placeholder={placeholder}
        disabled={disabled}
        readOnly
        fieldid={p.fieldid}
        value={date ? formatDate(date, p.subuitype, 'time', props.format) : ''}
        suffix={suffix ?? defaultSuffix}
        className={`${clsPrefix}-time-picker-input`}
        textAlign={textAlign}
      />
    </div>
  );

  const renderBrowser = (date: Date | string | null) => <div className={'mui-component-browser'}>
    <div className={classNames(`${clsPrefix}-time-picker-browser`)} {...(p.fieldid ? { fieldid: p.fieldid } : {})} >
      {date ? formatDate(date, p.subuitype, 'time', props.format) : '--'}
    </div>
  </div>

  if (browser) return withNativeProps(p, renderBrowser(value));

  if (disabled || readOnly) return withNativeProps(p, renderInput(value));

  return withNativeProps(p, (
    <div className={classPrefix}>
      <DatePicker
        title={popTitle}
        visible={visible}
        value={value}
        renderLabel={renderLabel}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onDismiss={handleDismiss}
        onClose={() => setVisible(false)}
        type="time"
        use12hours={p.use12Hours}
        filter={filterValue}
        precision={precision}
        fieldid={p.fieldid}
        showClear={p.showClear}
        popupStyle={p.popupStyle}
        popupClassName={p.popupClassName}
        getContainer={p.getContainer}
        cancelMode={p.cancelMode}
      >
        {renderInput}
      </DatePicker>
    </div>
  ))
}

export default TimePicker
